<script lang="ts">
  import CodeBlock from '../CodeBlock.svelte';
  import { countingCheck } from '../../lib/labs';

  /**
   * ラボ 1 の手順 0（labs.md）: 括弧の列を「数える」方法で判定し、`([)]` で誤ることを体験する。
   * 比喩は使わず、スタックという語で説明する。
   */
  interface Props {
    ondone: () => void;
  }

  let { ondone }: Props = $props();

  const SAMPLES = [
    { s: '(())', correct: true },
    { s: '())(', correct: false },
    { s: '([)]', correct: false },
  ] as const;

  let current = $state(0);
  let pos = $state(0);
  let finished: boolean[] = $state([false, false, false]);

  const sample = $derived(SAMPLES[current] ?? SAMPLES[0]);
  const trace = $derived(countingCheck(sample.s));
  const shownStep = $derived(pos === 0 ? null : (trace.steps[pos - 1] ?? null));
  const done = $derived(pos >= sample.s.length);
  const hasSquare = $derived(sample.s.includes('[') || sample.s.includes(']'));
  const unlocked = $derived(finished[2] === true);

  const CODE_ROUND = `var round = 0
var ok = true
for c in s {
    if c == "(" { round += 1 }
    if c == ")" { round -= 1 }
    if round < 0 { ok = false }
}
print(ok && round == 0 ? "Yes" : "No")`;
  const CODE_BOTH = `var round = 0, square = 0
var ok = true
for c in s {
    if c == "(" { round += 1 }
    if c == ")" { round -= 1 }
    if c == "[" { square += 1 }
    if c == "]" { square -= 1 }
    if round < 0 || square < 0 { ok = false }
}
print(ok && round == 0 && square == 0 ? "Yes" : "No")`;

  function choose(i: number) {
    current = i;
    pos = 0;
  }

  function advance(all = false) {
    pos = all ? sample.s.length : Math.min(pos + 1, sample.s.length);
    if (pos >= sample.s.length) finished[current] = true;
  }
</script>

<section class="panel intro" aria-labelledby="intro-title" data-testid="lab-intro">
  <h2 id="intro-title">まず、開きと閉じを数えて判定してみる</h2>
  <p>
    括弧だけの文字列が正しく閉じているかを判定します。開きで +1、閉じで −1
    と数え、途中で負にならず最後に 0 なら Yes とする方法です。
  </p>
  <div class="grid">
    <div class="col">
      <div class="choices" role="group" aria-label="文字列を選ぶ">
        {#each SAMPLES as smp, i (smp.s)}
          <button
            type="button"
            class="btn btn-sm mono"
            class:btn-primary={i === current}
            aria-pressed={i === current}
            onclick={() => choose(i)}
            >{smp.s}{#if finished[i]}<span class="done-mark">済</span>{/if}</button
          >
        {/each}
      </div>
      <p class="chars" aria-label="文字列 {sample.s}、{pos} 文字目まで読んだ">
        {#each [...sample.s] as ch, i (i)}
          <span class="ch" class:read={i < pos} class:now={i === pos - 1}>{ch}</span>
        {/each}
      </p>
      <dl class="counters" aria-live="polite">
        <div>
          <dt><code>(</code> <code>)</code> のカウンタ</dt>
          <dd class:neg={(shownStep?.round ?? 0) < 0}>{shownStep?.round ?? 0}</dd>
        </div>
        {#if hasSquare}
          <div>
            <dt><code>[</code> <code>]</code> のカウンタ</dt>
            <dd class:neg={(shownStep?.square ?? 0) < 0}>{shownStep?.square ?? 0}</dd>
          </div>
        {/if}
      </dl>
      <div class="row">
        <button type="button" class="btn btn-primary" onclick={() => advance()} disabled={done}
          >1 文字読む</button
        >
        <button type="button" class="btn" onclick={() => advance(true)} disabled={done}
          >最後まで読む</button
        >
      </div>
      {#if done}
        <p class="result" role="status">
          数える方法の答え: <strong>{trace.answer ? 'Yes' : 'No'}</strong>。 正しい答え:
          <strong>{sample.correct ? 'Yes' : 'No'}</strong>。
          {#if trace.answer === sample.correct}一致しています。{:else}食い違いました。{/if}
        </p>
      {/if}
    </div>
    <div class="col">
      <CodeBlock code={hasSquare ? CODE_BOTH : CODE_ROUND} label="数えて判定するコード" />
    </div>
  </div>
  {#if unlocked}
    <div class="reveal">
      <p>
        <code>([)]</code> はどちらのカウンタも 0 で終わるので、数える方法では Yes になります。
        正しくは No です。<code>(</code> の次に開いた <code>[</code> より先に、<code>)</code>
        で閉じています。
      </p>
      <p>
        カウンタの数だけでは「直前に開いた括弧はどれか」が分かりません。開き括弧をスタックに積み（<code
          >append</code
        >）、閉じ括弧が来たら
        <code>popLast()</code> で取り出して種類が合うかを照合します（LIFO）。
      </p>
      <button type="button" class="btn btn-primary" onclick={ondone} data-testid="intro-done"
        >スタックで解く</button
      >
    </div>
  {:else}
    <p class="text-soft small">3 つの文字列をすべて最後まで読むと、次へ進めます。</p>
  {/if}
</section>

<style>
  .intro {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .intro h2 {
    margin: 0;
  }
  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .choices,
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .mono,
  .chars {
    font-family: var(--font-mono);
  }
  .chars {
    display: flex;
    gap: var(--space-1);
    margin: 0;
    font-size: var(--fs-xl);
  }
  .ch {
    display: grid;
    place-items: center;
    width: 2.2rem;
    height: 2.6rem;
    border-radius: var(--radius-sm);
    border: 2px solid var(--border);
    color: var(--text-soft);
  }
  .ch.read {
    color: var(--text);
    border-color: var(--border-strong);
  }
  .ch.now {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .counters {
    display: flex;
    gap: var(--space-5);
    flex-wrap: wrap;
    margin: 0;
  }
  .counters div {
    display: flex;
    flex-direction: column;
  }
  .counters dt {
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .counters dd {
    margin: 0;
    font-size: var(--fs-2xl);
    font-weight: var(--fw-bold);
    font-variant-numeric: tabular-nums;
  }
  .counters dd.neg {
    color: var(--err-text);
  }
  .done-mark {
    margin-left: var(--space-1);
    font-family: var(--font-sans);
    font-size: var(--fs-xs);
  }
  .result {
    margin: 0;
  }
  .reveal {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: flex-start;
    padding: var(--space-4);
    border-radius: var(--radius-md);
    background: var(--accent-soft);
  }
  .reveal p {
    margin: 0;
  }
  .small {
    font-size: var(--fs-sm);
  }
  @media (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
